import { useState } from "react";
import { Button } from "@mui/material";

import "./style.scss";
import { Provider } from "../../../common/interfaces/admin/provider.interface";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import CrawlSettingsForm from "../../crawl-settings/crawl-settings-form.comp";
import {
    CrawlSettingsValues,
    crawlSettingsFromProvider,
    toCrawlConfig,
    validateCrawlSettings
} from "../../crawl-settings/crawl-settings.util";
import { updateCrawlConfig } from "../../../api/admin/content-providers/endpoints-referrers";

/**
 * "Edit crawl settings" (web_scraper_provider_design.md §10): the same
 * fields as the create form, saved with PATCH …/crawl-config; the change
 * applies on the next crawl.
 */
export const CrawlSettingsDialog = ({
    open,
    companyId,
    provider,
    handleClose,
    handleSaved
}: {
    open: boolean;
    companyId: number;
    provider: Provider;
    handleClose: () => void;
    handleSaved: () => void;
}) => {
    const [values, setValues] = useState<CrawlSettingsValues>(() =>
        crawlSettingsFromProvider(provider)
    );
    const [showErrors, setShowErrors] = useState(false);
    const [serverError, setServerError] = useState("");
    const [busy, setBusy] = useState(false);
    const errors = validateCrawlSettings(values);

    const save = () => {
        if (errors.length) {
            setShowErrors(true);
            return;
        }
        setBusy(true);
        setServerError("");
        updateCrawlConfig(companyId, provider.id, {
            crawlConfig: toCrawlConfig(values, "edit"),
            reviewPolicy: values.reviewPolicy,
            crawlSchedule: values.crawlSchedule,
            robotsOverride: values.robotsOverride && values.robotsAttested
        })
            .then(() => handleSaved())
            .catch(error => {
                const message = error?.response?.data?.message;
                setServerError(
                    typeof message === "string" ? message : "The settings could not be saved."
                );
            })
            .finally(() => setBusy(false));
    };

    return (
        <ModalTemplate
            open={open}
            width={654}
            handleClose={handleClose}
            ariaLabel="Edit crawl settings"
        >
            <div className="crawl-settings-dialog">
                <h2 className="crawl-settings-dialog__title">Edit crawl settings</h2>
                <p className="crawl-settings-dialog__sub">
                    {`${provider.displayName}. Changes apply to the next crawl.`}
                </p>
                <div className="crawl-settings-dialog__body">
                    <CrawlSettingsForm
                        values={values}
                        onChange={setValues}
                        mode="edit"
                        disabled={busy}
                    />
                    {showErrors && errors.length > 0 && (
                        <div className="crawl-settings-dialog__error" role="alert">
                            <ul>
                                {errors.map(error => (
                                    <li key={error}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {serverError && (
                        <div className="crawl-settings-dialog__error" role="alert">
                            {serverError}
                        </div>
                    )}
                </div>
                <div className="crawl-settings-dialog__actions">
                    <Button
                        className="thirt-button"
                        variant="text"
                        onClick={handleClose}
                        disabled={busy}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="primary-button"
                        variant="text"
                        onClick={save}
                        disabled={busy}
                    >
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>
        </ModalTemplate>
    );
};

export default CrawlSettingsDialog;
